import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {derivedThemeAtom} from 'atoms/layout';
import {getStatusStyle} from 'components/DSYS/StatusButtonIcon';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {forwardRef, ReactNode} from 'react';

import {
  EarningsComparison,
  EarningsDeltaStatus,
  EarningsMetric,
  QuarterEarnings,
} from './earningsData';
import {EarningsInfoTooltip} from './EarningsInfoTooltip';
import styles from './EarningsTile.module.css';

/**
 * Chips reuse the workstream status palette (and StatusChip's light/dark tint)
 * so a "beat" reads as the same green as "On track" everywhere else.
 */
const BADGE_STYLES: Record<EarningsDeltaStatus, {status: string; icon: string; srKey: string}> = {
  beat: {status: 'on_track', icon: '▲', srKey: 'dashboard_earnings_beat'},
  inline: {status: 'at_risk', icon: '●', srKey: 'dashboard_earnings_in_line'},
  miss: {status: 'off_track', icon: '▼', srKey: 'dashboard_earnings_miss'},
  neutral: {status: 'cancelled', icon: '●', srKey: 'dashboard_earnings_neutral'},
};

const Chip = ({status, icon, children}: {status: string; icon?: string; children: ReactNode}) => {
  const isDark = useAtomValue(derivedThemeAtom) !== 'bright';
  const {color} = getStatusStyle(status);
  return (
    <Box
      display="flex"
      alignItems="center"
      borderRadius="XLarge"
      paddingX="10"
      paddingY="4"
      className={styles.chip}
      // Same tint math as StatusChip: status color at 13% (light) / 31% (dark).
      style={{backgroundColor: color + (isDark ? '50' : '20')}}
    >
      {icon && (
        <span aria-hidden className={styles.chipIcon} style={{color}}>
          {icon}
        </span>
      )}
      {children}
    </Box>
  );
};

const DeltaBadge = ({comparison}: {comparison: EarningsComparison}) => {
  const s = BADGE_STYLES[comparison.status];
  return (
    <Chip status={s.status} icon={s.icon}>
      <Text size="small" isBold>
        <span className={styles.srOnly}>{t(s.srKey)}: </span>
        {comparison.text}
      </Text>
    </Chip>
  );
};

/** Raised-guidance pill, matching the beat badge. */
const RaisedBadge = () => (
  <Chip status="on_track" icon="▲">
    <Text size="xsmall" isBold>
      {t('dashboard_earnings_raised')}
    </Text>
  </Chip>
);

const Eyebrow = ({children}: {children: ReactNode}) => (
  <Text size="xsmall" isBold color="faint" tagName="div" className={styles.eyebrow}>
    {children}
  </Text>
);

/** Supporting context below a hairline, one fact per line like tile row subtext. */
const Facts = ({facts, note}: {facts: string[]; note?: string}) => (
  <Stack gap="4" className={styles.facts}>
    {facts.map((fact) => (
      <Text key={fact} size="small" color="faint" tagName="div">
        {fact}
      </Text>
    ))}
    {note && (
      <Text size="small" color="faint" tagName="div" style={{fontStyle: 'italic'}}>
        {note}
      </Text>
    )}
  </Stack>
);

/** One page of the carousel; `forwardRef` so the tile can scroll to it. */
const Slide = forwardRef<HTMLDivElement, {children: ReactNode; label: string}>(
  ({children, label}, ref) => (
    <div
      ref={ref}
      className={styles.slide}
      role="group"
      aria-roledescription="slide"
      aria-label={label}
    >
      {children}
    </div>
  )
);
Slide.displayName = 'Slide';

export const MetricSlide = forwardRef<HTMLDivElement, {metric: EarningsMetric}>(({metric}, ref) => (
  <Slide ref={ref} label={metric.label}>
    <Stack gap="12">
      <Stack gap="4">
        <Box display="flex" alignItems="center" style={{gap: 4}}>
          <Eyebrow>{metric.label}</Eyebrow>
          <EarningsInfoTooltip
            title={
              metric.nonGaap
                ? `${metric.tooltip} ${t('dashboard_earnings_non_gaap_note')}`
                : metric.tooltip
            }
          />
        </Box>
        <div className={styles.value}>{metric.value}</div>
      </Stack>
      <Stack gap="8">
        <Stack gap="6">
          <DeltaBadge comparison={metric.primary} />
          {metric.secondary && !Array.isArray(metric.secondary) && (
            <DeltaBadge comparison={metric.secondary} />
          )}
        </Stack>
        {(Array.isArray(metric.secondary) || metric.note) && (
          <Facts
            facts={Array.isArray(metric.secondary) ? metric.secondary : []}
            note={metric.note}
          />
        )}
      </Stack>
    </Stack>
  </Slide>
));
MetricSlide.displayName = 'MetricSlide';

export const OutlookSlide = forwardRef<HTMLDivElement, {guidance: QuarterEarnings['guidance']}>(
  ({guidance}, ref) => (
    <Slide ref={ref} label={t('dashboard_earnings_outlook').toString()}>
      <Stack gap="12">
        <Eyebrow>{t('dashboard_earnings_outlook')}</Eyebrow>
        {guidance.map((g) => (
          <Stack key={g.period} gap="6">
            <Text size="small" isBold tagName="div">
              {g.period}
            </Text>
            {g.items.map((it) => (
              <Box
                key={it.text}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{gap: 8}}
              >
                <Text size="small">{it.text}</Text>
                {it.raised && <RaisedBadge />}
              </Box>
            ))}
          </Stack>
        ))}
      </Stack>
    </Slide>
  )
);
OutlookSlide.displayName = 'OutlookSlide';
